import { Injectable } from '@angular/core'
import { Draw, Modify } from 'ol/interaction'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style } from 'ol/style'
import CircleStyle from 'ol/style/Circle'
import { MapInfoBoxTypeEnum } from '../../features/map/info-box/map-info-box-type.enum'
import VectorLayer from 'ol/layer/Vector'
import { Map } from 'ol'
import { IMapStateMeasureDrawing } from '../../features/map/interfaces/map-state.interface'
import BaseEvent from 'ol/events/Event'
import { DrawEvent } from 'ol/interaction/Draw'
import { ModifyEvent } from 'ol/interaction/Modify'

@Injectable({
    providedIn: 'root',
})
export class OpenLayersMapMeasureService {
    private readonly deleteLastPoint = 'deleteLastPoint'

    private modifyStyle = new Style({
        image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
                color: 'rgba(0, 0, 0, 0.7)',
            }),
            fill: new Fill({
                color: 'rgba(0, 0, 0, 0.4)',
            }),
        }),
    })

    private vectorStyle = new Style({
        fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new Stroke({
            color: 'rgba(0, 0, 0, 1)',
            width: 2,
        }),
        image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
                color: 'rgba(0, 0, 0, 0.7)',
            }),
            fill: new Fill({
                color: 'rgba(0, 0, 0, 1)',
            }),
        }),
    })

    private drawStyle = new Style({
        fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.5)',
            lineDash: [10, 10],
            lineCap: 'butt',
            width: 2,
        }),
        image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
                color: 'rgba(0, 0, 0, 1)',
            }),
            fill: new Fill({
                color: 'rgba(255, 255, 255, 0.2)',
            }),
        }),
    })

    public convertArea(num: number): string {
        // by default, turf returns area in square metres
        if (num) {
            let area = num
            let units = 'km'

            if (area > 1000000) {
                area /= 1000000
            } else {
                units = 'm'
            }

            return area.toFixed(2) + ' ' + units
        } else {
            return '0'
        }
    }

    public convertDistance(num: number): string {
        // by default, turf returns distance in km
        if (num) {
            let distance = num
            let units = 'km'

            if (distance < 1000) {
                units = 'm'
            } else {
                distance /= 1000
            }

            return distance.toFixed(2) + ' ' + units
        } else {
            return '0'
        }
    }

    public createDrawing(
        type: MapInfoBoxTypeEnum,
        map: Map,
        drawingGeometryChange: (evt: BaseEvent) => any,
        modifyingGeometryChange: (evt: BaseEvent) => any,
    ): IMapStateMeasureDrawing {
        const source = new VectorSource()
        const modify = new Modify({
            source: source,
            style: this.modifyStyle,
        })
        const vector = new VectorLayer({
            source: source,
            style: this.vectorStyle,
            zIndex: 1000,
        })

        const draw = new Draw({
            source: source,
            type: type === MapInfoBoxTypeEnum.measure_distance ? 'LineString' : 'Polygon',
            style: this.drawStyle,
        })

        draw.set(this.deleteLastPoint, false)

        draw.on('drawstart', (evt: DrawEvent) => {
            source.clear()
            modify.setActive(false)
            draw.set(this.deleteLastPoint, true)

            evt.feature.getGeometry().on('change', drawingGeometryChange)
        })

        draw.on('drawend', () => {
            modify.setActive(true)
            draw.setActive(false)
            draw.set(this.deleteLastPoint, false)
        })

        draw.on('drawabort', () => {
            draw.set(this.deleteLastPoint, false)
            draw.setActive(true)
        })

        modify.setActive(true)

        modify.on('modifystart', (evt: ModifyEvent) => {
            if (evt.features.getLength() > 0) {
                evt.features.item(0).getGeometry().on('change', modifyingGeometryChange)
            }
        })

        map.addInteraction(modify)
        map.addInteraction(draw)
        map.addLayer(vector)

        return {
            draw: draw,
            modify: modify,
            vectorLayer: vector,
        }
    }

    public hasDrawingLastPoint(draw: Draw): boolean {
        return draw.get(this.deleteLastPoint)
    }
}